import { useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Upload, X, Loader2, Star } from "lucide-react";
import { toast } from "sonner";

/**
 * Iter 20 — Multi-photo uploader for catalog items (e.g. different colours / angles
 * of the same item).
 *
 * `value` is an array of stored object-storage paths.
 * `onChange` receives the new array on every add/remove/reorder.
 *
 * UI:
 *   - Grid of thumbnails (drag-handle-less but click ⭐ on a non-first photo
 *     to promote it to "main").
 *   - "Tambah foto" tile appended at the end.
 *   - Hard cap to keep storage costs sane.
 */
export default function MultiImageUploader({
  value = [],
  onChange,
  testid = "multi-image-uploader",
  max = 8,
}) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const photos = Array.isArray(value) ? value : [];

  const uploadMany = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const incoming = Array.from(fileList);
    const remaining = max - photos.length;
    if (remaining <= 0) {
      toast.error(`Maksimal ${max} foto per item`);
      return;
    }
    const trimmed = incoming.slice(0, remaining);
    if (incoming.length > remaining) {
      toast.warning(`Hanya ${remaining} foto pertama dipakai (batas ${max})`);
    }
    setUploading(true);
    const added = [];
    try {
      for (const file of trimmed) {
        const form = new FormData();
        form.append("file", file);
        try {
          const r = await api.post("/uploads/image", form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (r.data?.path) added.push(r.data.path);
        } catch (err) {
          console.warn("[MultiImageUploader] upload failed for", file.name, err);
        }
      }
      if (added.length > 0) {
        onChange([...photos, ...added]);
        toast.success(
          added.length === 1 ? "Foto terunggah" : `${added.length} foto terunggah`
        );
      } else if (trimmed.length > 0) {
        toast.error("Gagal mengunggah foto");
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (idx) => {
    const next = photos.filter((_, i) => i !== idx);
    onChange(next);
  };

  const promoteToMain = (idx) => {
    if (idx === 0) return;
    const next = [photos[idx], ...photos.filter((_, i) => i !== idx)];
    onChange(next);
    toast.success("Diset sebagai foto utama");
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => uploadMany(e.target.files)}
        data-testid={`${testid}-input`}
      />
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {photos.map((p, i) => (
          <div
            key={`${p}-${i}`}
            className="relative aspect-square border group overflow-hidden"
            style={{ borderColor: i === 0 ? "var(--app-brand)" : "var(--app-border)" }}
            data-testid={`${testid}-thumb-${i}`}
          >
            <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
            {i === 0 && (
              <div
                className="absolute top-1 left-1 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide text-white rounded-sm"
                style={{ backgroundColor: "var(--app-brand)" }}
                data-testid={`${testid}-main-badge`}
              >
                Utama
              </div>
            )}
            <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {i !== 0 && (
                <button
                  type="button"
                  onClick={() => promoteToMain(i)}
                  className="w-6 h-6 bg-white border flex items-center justify-center hover:bg-[var(--app-brand)] hover:text-white"
                  style={{ borderColor: "var(--app-border)" }}
                  title="Jadikan foto utama"
                  data-testid={`${testid}-promote-${i}`}
                >
                  <Star size={12} strokeWidth={2} />
                </button>
              )}
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="w-6 h-6 bg-white border flex items-center justify-center hover:bg-red-600 hover:text-white"
                style={{ borderColor: "var(--app-border)" }}
                title="Hapus foto"
                data-testid={`${testid}-remove-${i}`}
              >
                <X size={12} strokeWidth={2} />
              </button>
            </div>
          </div>
        ))}

        {photos.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="aspect-square border border-dashed flex flex-col items-center justify-center gap-1 text-xs hover:bg-[var(--app-surface-alt)] transition-colors"
            style={{ borderColor: "var(--app-border)", color: "var(--app-muted)" }}
            data-testid={`${testid}-add-btn`}
          >
            {uploading ? (
              <Loader2 size={18} strokeWidth={1.5} className="animate-spin" />
            ) : (
              <Upload size={18} strokeWidth={1.5} />
            )}
            <span>{uploading ? "Mengunggah..." : "Tambah foto"}</span>
            <span className="text-[10px] font-mono">{photos.length}/{max}</span>
          </button>
        )}
      </div>
      {photos.length === 0 && !uploading && (
        <p className="text-xs" style={{ color: "var(--app-muted)" }}>
          Tambah beberapa foto bila item Anda ada beberapa varian warna/ukuran.
        </p>
      )}
    </div>
  );
}
